class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onServiceResultClick.bind(this));
  }

  onServiceResultClick(event) {
    const link = event.target.closest('a[data-service-id]');
    if (!link) return;

    sessionStorage.setItem('serviceId', link.dataset.serviceId || '');
    sessionStorage.setItem('selectedGender', link.dataset.serviceGender || '');
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then(async (text) => {
        const parser = new DOMParser();
        const resultsMarkup = parser.parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        const enrichedMarkup = await this.addServiceResultsToMarkup(resultsMarkup, searchTerm);

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = enrichedMarkup;
        });
        this.renderSearchResults(enrichedMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  async addServiceResultsToMarkup(resultsMarkup, searchTerm) {
    try {
      const endpoints = [
        { gender: 'Womens services', detailPage: 'womens' },
        { gender: "Men's services", detailPage: 'mens' },
      ];

      const serviceResults = await Promise.all(
        endpoints.map(async (endpoint) => {
          try {
            const response = await fetch(
              `https://catalogapi.hulsecure.in/api/website/service/service-list?gender=${encodeURIComponent(endpoint.gender)}`
            );
            const result = await response.json();

            if (!result?.success || !Array.isArray(result.data)) {
              return [];
            }

            return result.data
              .filter((service) => (service.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
              .map((service) => ({
                ...service,
                gender: endpoint.gender,
                detailPage: endpoint.detailPage,
              }));
          } catch (error) {
            console.error('Service predictive search fetch failed:', error);
            return [];
          }
        })
      );

      const matches = serviceResults.flat().slice(0, 5);
      const parser = new DOMParser();
      const doc = parser.parseFromString(resultsMarkup, 'text/html');

      if (matches.length) {
        const group = document.createElement('div');
        group.className = 'predictive-search__result-group';

        const heading = document.createElement('h2');
        heading.id = 'predictive-search-services';
        heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
        heading.textContent = 'Services';
        group.appendChild(heading);

        const list = document.createElement('ul');
        list.id = 'predictive-search-results-services-list';
        list.className = 'predictive-search__results-list list-unstyled';
        list.setAttribute('role', 'group');
        list.setAttribute('aria-labelledby', 'predictive-search-services');

        matches.forEach((service, index) => {
          const titleSlug = (service.name || '')
            .trim()
            .replace(/\s+/g, '')
            .replace(/[^a-zA-Z0-9-]/g, '');
          const href = `/pages/${service.detailPage}?${titleSlug}`;
          const li = document.createElement('li');
          li.id = `predictive-search-option-service-${index}`;
          li.className = 'predictive-search__list-item';
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');

          const a = document.createElement('a');
          a.href = href;
          a.className = 'predictive-search__item link link--text';
          a.tabIndex = -1;
          a.dataset.serviceId = service.id || '';
          a.dataset.serviceGender = service.gender || service.detailPage || '';
          const content = document.createElement('div');
          content.className = 'predictive-search__item-content predictive-search__item-content--centered';

          const serviceName = document.createElement('p');
          serviceName.className = 'predictive-search__item-heading h5';
          serviceName.textContent = service.name || 'Service';

          const serviceGender = document.createElement('span');
          serviceGender.className = 'predictive-search__item-heading h5';
          serviceGender.textContent = service.detailPage === 'mens' ? ' (Men)' : ' (Women)';

          serviceName.appendChild(serviceGender);
          content.appendChild(serviceName);
          a.appendChild(content);

          li.appendChild(a);
          list.appendChild(li);
        });

        group.appendChild(list);

        const wrapper = doc.querySelector('#predictive-search-results-groups-wrapper');
        if (wrapper) {
          wrapper.appendChild(group);
        } else {
          doc.body.appendChild(group);
        }
      }

      return doc.body.innerHTML;
    } catch (error) {
      console.error('Service predictive search failed:', error);
      return resultsMarkup;
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
